import { useEffect, useRef, useState } from 'react';
import { Check, FileText, Loader2, Upload } from 'lucide-react';
import api from '../../utils/api';
import { getErrorMessage } from '../../utils/errorMessage';
import { MAX_BREAKDOWN_CHARS, MAX_EXTERNAL_SCRIPT_CHARS, MAX_SCRIPT_FILE_BYTES, readScriptTextFile } from './externalScriptText';

export default function ExternalScriptImport({ draft, selection, onChange, onReadingChange, disabled, preview }) {
  const inputRef = useRef(null);
  const requestRef = useRef(null);
  const [reading, setReading] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [dragging, setDragging] = useState(false);
  const [replacement, setReplacement] = useState(null);
  const notifyReading = useRef(onReadingChange);
  notifyReading.current = onReadingChange;
  useEffect(() => () => { requestRef.current?.abort(); notifyReading.current(false); }, []);

  const importFile = async file => {
    if (disabled || reading || requestRef.current || !file) return;
    setError(''); setNotice(''); setReplacement(null);
    const controller = new AbortController(); requestRef.current = controller;
    setReading(true); onReadingChange(true);
    try {
      let text;
      if (/\.txt$/i.test(file.name)) text = await readScriptTextFile(file);
      else {
        if (preview) throw new Error('界面预览支持粘贴正文和 TXT；请登录后上传 Word 剧本。');
        const form = new FormData(); form.append('file', file);
        const { data } = await api.post('/canvas/script-import/parse', form, { signal: controller.signal });
        if (typeof data?.text !== 'string') throw new Error('没有读取到剧本正文，请重新选择文件。');
        text = data.text;
      }
      if (controller.signal.aborted) return;
      if (!text.trim()) throw new Error('没有读取到正文，请检查文件，或直接粘贴剧本。');
      if (text.length > MAX_EXTERNAL_SCRIPT_CHARS) throw new Error('剧本超过 20 万字，请按集分成多个文件后导入。');
      onChange({ text, fileName: file.name, selectedId: '' });
      setNotice('正文已读取，可直接修改；确认后再开始拆镜。');
    } catch (failure) {
      if (!controller.signal.aborted) setError(getErrorMessage(failure, '读取剧本失败，请重试或直接粘贴正文。'));
    } finally {
      if (requestRef.current === controller) requestRef.current = null;
      if (!controller.signal.aborted) { setReading(false); onReadingChange(false); }
    }
  };
  const chooseFiles = files => {
    if (disabled || reading || !files?.length) return;
    setError(''); setNotice('');
    if (files.length !== 1) { setError('一次导入一个剧本文件。'); return; }
    const file = files[0];
    if (!/\.(docx?|txt)$/i.test(file.name)) { setError('支持 Word（.docx、.doc）和 TXT；其他格式请先复制正文粘贴。'); return; }
    if (file.size > MAX_SCRIPT_FILE_BYTES) { setError('文件超过 5MB，请精简文件或按集导入。'); return; }
    if (draft.text.trim()) { setReplacement(file); return; }
    importFile(file);
  };
  const locked = disabled || reading;
  return <section className="canvas-external-script" aria-label="导入外部剧本">
    <div className={`canvas-script-upload${dragging ? ' is-dragging' : ''}`} aria-busy={reading}
      onDragOver={event => { event.preventDefault(); if (!locked) setDragging(true); }}
      onDragLeave={event => { if (!event.currentTarget.contains(event.relatedTarget)) setDragging(false); }}
      onDrop={event => { event.preventDefault(); setDragging(false); chooseFiles(Array.from(event.dataTransfer.files || [])); }}>
      <span className="canvas-script-upload-icon" aria-hidden="true">{reading ? <Loader2 size={21} className="animate-spin" /> : <FileText size={21} />}</span>
      <div><strong>{reading ? '正在读取剧本…' : draft.fileName || '把已有的故事，带进画布'}</strong>
        <p>{reading ? '正在提取正文，请稍候' : '拖入 Word / TXT，或在下方直接粘贴 · 最大 5MB'}</p></div>
      <button type="button" disabled={locked} onClick={() => inputRef.current?.click()}><Upload size={13} />{draft.fileName ? '更换文件' : '上传剧本'}</button>
      <input ref={inputRef} type="file" accept=".docx,.doc,.txt" aria-label="上传外部剧本文件" hidden disabled={locked}
        onChange={event => { chooseFiles(Array.from(event.target.files || [])); event.target.value = ''; }} />
    </div>
    {replacement && <div className="canvas-script-replace" role="alert"><span>读取「{replacement.name}」会替换当前正文。</span><div>
      <button type="button" disabled={locked} onClick={() => setReplacement(null)}>保留正文</button>
      <button type="button" disabled={locked} onClick={() => importFile(replacement)}>替换并读取</button>
    </div></div>}
    {error && <p className="canvas-external-error" role="alert">{error}</p>}
    {notice && <p className="canvas-external-notice" role="status"><Check size={13} />{notice}</p>}
    <div className="canvas-script-editor-head"><label htmlFor="canvas-external-script-text">剧本正文</label><span>{draft.text.length.toLocaleString()} / 200,000 字</span></div>
    <textarea id="canvas-external-script-text" value={draft.text} disabled={locked} aria-describedby="canvas-external-script-help"
      placeholder={'粘贴你的剧本，或上传文件后在这里预览、修改。\n\n第1集：雨夜来信\n场景一  码头 / 夜 / 外\n林乔推开仓库的门，手里攥着一封旧信。\n林乔：你终于来了。'}
      onChange={event => { onChange({ ...draft, text: event.target.value }); setNotice(''); }} />
    <p id="canvas-external-script-help" className="canvas-script-editor-help">支持「第1集」「第一集」分集标题；原文可编辑，创建时保存本次选中的正文。</p>
    {draft.text.length > MAX_EXTERNAL_SCRIPT_CHARS && <p className="canvas-external-error" role="alert">正文超过 20 万字，请先精简后再创建。</p>}
    {selection.multiple && <div className="canvas-external-episode">
      <label className="canvas-create-field"><span>识别到 {selection.episodes.length} 集 · 本次制作</span><select aria-label="外部剧本制作哪一集" value={selection.selectedEpisode?.id || ''} disabled={locked}
        onChange={event => onChange({ ...draft, selectedId: event.target.value })}>
        <option value="">选择一集，开始这次制作</option>{selection.episodes.map(episode => <option key={episode.id} value={episode.id}>{episode.title}</option>)}
      </select></label>
      {selection.preamble && <p>文件开头的背景说明会一并带入。</p>}
    </div>}
    {selection.content && <div className="canvas-external-selection" aria-label="本次带入正文"><strong>{selection.episodeLabel || '本次剧本'}<span>{selection.content.length.toLocaleString()} 字</span></strong>
      {selection.multiple && <p>{selection.selectedEpisode.content}</p>}
      <small>{selection.content.length > MAX_BREAKDOWN_CHARS ? '单次拆镜最多 8,000 字。请精简正文，或先选择「仅创建画布」。' : '确认正文后自动拆成可编辑分镜，图片和视频另行生成。'}</small>
    </div>}
  </section>;
}
